import { useEffect, useRef } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useNavigate,
	useParams,
	useRouteError,
} from 'react-router'
import Logo from '#app/components/logo.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import { getErrorMessage } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const navigate = useNavigate()

		return (
			<div className={'flex flex-col gap-sm'}>
				<Text as={'h1'} size={'display'} type={'serif'} weight={'light'}>
					{error?.status ?? 'Error'}
				</Text>
				<Text as={'p'} size={'large'} weight={'light'}>
					{error?.data ?? 'Something went wrong. Please try again.'}
				</Text>
				<Button
					onClick={() => {
						void navigate(-1)
					}}
					variant={'secondary'}
					className={'self-start'}
				>
					<Icon name={'arrow-left'} size={'lg'} />
				</Button>
			</div>
		)
	},
	statusHandlers,
	unexpectedErrorHandler = (error) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const navigate = useNavigate()

		return (
			<div className={'flex flex-col gap-sm'}>
				<Text as={'h1'} size={'display'} type={'serif'} weight={'light'}>
					Server Error
				</Text>
				<Text as={'p'} size={'large'} weight={'light'}>
					{getErrorMessage(error) ?? 'Something went wrong. Please try again.'}
				</Text>
				<Button
					onClick={() => {
						void navigate(-1)
					}}
					variant={'secondary'}
					className={'self-start'}
				>
					<Icon name={'arrow-left'} size={'lg'} />
				</Button>
			</div>
		)
	},
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	const params = useParams()
	const hasLoggedRef = useRef(false)

	if (typeof document !== 'undefined') {
		console.error(JSON.stringify(error, null, 2))
	}

	// Client-side error logging (with deduplication)
	useEffect(() => {
		if (typeof window !== 'undefined' && !hasLoggedRef.current) {
			// Expected navigation outcomes (404, 401, ...) are not application
			// errors — only report route responses that indicate a server failure.
			if (isRouteErrorResponse(error) && error.status < 500) {
				return
			}
			hasLoggedRef.current = true
			// Send to your logging endpoint. This is the only signal the
			// server gets for network-level failures (e.g. "Failed to
			// fetch") since, by definition, the server never received a
			// usable request for those.
			const connection = (navigator as any).connection as
				| { effectiveType?: string }
				| undefined

			fetch('/resources/log-client-error', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					error: getErrorMessage(error),
					url: window.location.href,
					userAgent: navigator.userAgent,
					online: navigator.onLine,
					connectionType: connection?.effectiveType,
				}),
			}).catch(() => {}) // Silent fail
		}
	}, [error])

	return (
		<div
			data-testid={'error-boundary'}
			className="container relative flex h-screen min-h-screen items-center justify-center overflow-x-hidden"
		>
			<LocalizedLink to={routes.public.base.route({})}>
				<Logo
					className={
						'fixed left-1/2 top-md h-[clamp(25px,4rem,80px)] -translate-x-1/2'
					}
				/>
			</LocalizedLink>

			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
